import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthProviderWrapper';
import { Header } from './Header';
import Footer from '../../components/Footer';
import { SkipLinks } from './design-system/SkipLinks';
import { useSafeAreaInsets } from './navigation/hooks/useSafeAreaInsets';
import { useNavigationVisibility } from './navigation/hooks/useNavigationVisibility';
import CommunityHeader from './community/CommunityHeader';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * App Layout Component
 * Provides the main layout structure with Header for desktop
 * and allows IOSTabLayout to handle mobile navigation
 *
 * The Header is only visible on desktop (hidden on mobile/iPad)
 * Mobile devices will show bottom tabs instead
 */
export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { safeAreaInsets, isIOS } = useSafeAreaInsets();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { shouldShowMobileNav } = useNavigationVisibility();
  
  // Check if we're on a community page
  const isCommunityPage = location.pathname.startsWith('/app/community');

  // Calculate the top position for the sticky header on iOS
  const headerStyle: React.CSSProperties = isIOS && safeAreaInsets.top > 0 ? {
    top: `${safeAreaInsets.top}px`
  } : {
    top: 0
  };

  // Determine whether to show the header:
  // - Don't show while auth is loading (prevent flicker)
  // - For logged-out users on community: don't show (CommunityHeader handles it)
  // - For logged-in users: always show on desktop, never on mobile
  const shouldShowHeader = !isLoading && (!isCommunityPage || isAuthenticated);

  // Only show Header on desktop - hide it completely on mobile/iPad
  // Mobile navigation is handled by IOSTabLayout's BottomTabs
  const shouldShowDesktopHeader = shouldShowHeader && !shouldShowMobileNav;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'rgba(230, 239, 234, 1)' }}>
      {/* Skip navigation links for accessibility */}
      <SkipLinks />

      {/* Community Header for logged-out users - positioned outside content */}
      {isCommunityPage && !isAuthenticated && !isLoading && (
        <CommunityHeader />
      )}

      {/* Header - Only visible on desktop (not on mobile/iPad) */}
      {shouldShowDesktopHeader && (
        <div className="sticky z-50" style={headerStyle}>
          <header role="banner">
            <Header showAddRecipe={true} />
          </header>
        </div>
      )}
      
      {/* Main content area */}
      <main
        id="main-content"
        role="main"
        className="w-full flex-grow"
        style={isCommunityPage && !isLoading && !isAuthenticated ? { paddingTop: '64px' } : {}}
      >
        {children}
      </main>
      
      {/* Footer - Visible on all screen sizes */}
      <footer role="contentinfo">
        <Footer />
      </footer>
    </div>
  );
};